import React, { useRef, useEffect, useState, useCallback } from "react";
import styles from "./AudioWaveform.module.scss";
import logger from "../../../../utils/logger";

interface AudioWaveformProps {
  audioBlob?: Blob;
  audioUrl?: string;
  waveformData?: number[]; // Dados pré-processados da waveform
  isFromMe?: boolean;
  isDarkTheme?: boolean; // Adicionar propriedade para tema
  currentTime?: number;
  duration?: number;
  onSeek?: (time: number) => void;
  className?: string;
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({
  audioBlob,
  audioUrl,
  waveformData,
  isFromMe = false,
  isDarkTheme = true, // Valor padrão
  currentTime = 0,
  duration = 0,
  onSeek,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [waveformDataState, setWaveformDataState] = useState<number[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const generateDefaultWaveform = useCallback(() => {
    const samples = 60; // Quantidade de barras similar ao WhatsApp
    const defaultData = Array.from({ length: samples }, (_, i) => {
      const position = i / samples;

      // Criar padrão mais realista similar ao áudio real
      // Barras menores no início e fim, variações no meio
      if (position < 0.15 || position > 0.85) {
        // Início e fim: barras baixas (silêncio)
        return 2 + Math.random() * 2;
      } else if (position > 0.2 && position < 0.3) {
        // Início da fala: crescimento gradual
        return 3 + Math.random() * 4;
      } else if (position > 0.4 && position < 0.6) {
        // Meio: pico de volume (fala principal)
        return 6 + Math.random() * 8;
      } else if (position > 0.65 && position < 0.75) {
        // Continuação: variação média
        return 4 + Math.random() * 6;
      } else {
        // Transições: barras médias
        return 3 + Math.random() * 5;
      }
    });
    setWaveformDataState(defaultData);
  }, []);

  const analyzeAudio = useCallback(async () => {
    if (!audioBlob && !audioUrl) return;

    setIsAnalyzing(true);
    try {
      const audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      let audioBuffer: AudioBuffer;

      if (audioBlob) {
        const arrayBuffer = await audioBlob.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      } else if (audioUrl) {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      } else {
        return;
      }

      const channelData = audioBuffer.getChannelData(0);
      const samples = 60; // Número de barras do waveform
      const blockSize = Math.floor(channelData.length / samples);
      const filteredData: number[] = [];

      for (let i = 0; i < samples; i++) {
        const blockStart = blockSize * i;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(channelData[blockStart + j]);
        }
        const average = sum / blockSize;

        // Normalizar e aplicar diferentes alturas baseadas na amplitude
        const normalizedAverage = average * 100;

        // Se a amplitude é muito baixa (silêncio), usar altura mínima
        if (normalizedAverage < 0.5) {
          filteredData.push(2); // Altura mínima para silêncio (linha "lisa")
        } else if (normalizedAverage < 2) {
          filteredData.push(4 + Math.random() * 2); // Baixa amplitude
        } else if (normalizedAverage < 5) {
          filteredData.push(6 + Math.random() * 4); // Média amplitude
        } else {
          filteredData.push(8 + Math.random() * 8); // Alta amplitude (ondulações)
        }
      }

      setWaveformDataState(filteredData);
    } catch (error) {
      logger.error("Erro ao analisar áudio:", error);
      // Gerar waveform padrão em caso de erro
      generateDefaultWaveform();
    } finally {
      setIsAnalyzing(false);
    }
  }, [audioBlob, audioUrl, generateDefaultWaveform]);

  useEffect(() => {
    // Se temos dados pré-processados, usar eles
    if (waveformData && waveformData.length > 0) {
      setWaveformDataState(waveformData);
      setIsAnalyzing(false);
    } else if (audioBlob || audioUrl) {
      // Caso contrário, analisar o áudio
      analyzeAudio();
    }
  }, [audioBlob, audioUrl, waveformData, analyzeAudio]);

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onSeek || !duration || !isFinite(duration) || duration <= 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;

    // Validar coordenadas do clique
    if (!isFinite(x) || !isFinite(rect.width) || rect.width <= 0) return;

    const clickPercentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = clickPercentage * duration;

    // Validar o tempo calculado antes de enviar
    if (!isFinite(newTime) || newTime < 0 || newTime > duration) return;

    onSeek(newTime);
  };

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const barCount = waveformDataState.length;
    const barSpacing = 1; // Espaçamento mínimo entre barras (similar ao WhatsApp)
    const barWidth = Math.max(
      1,
      (width - (barCount - 1) * barSpacing) / barCount,
    );
    const progress = duration > 0 ? currentTime / duration : 0;

    // Limpar canvas
    ctx.clearRect(0, 0, width, height);

    // Desenhar barras do waveform
    waveformDataState.forEach((amplitude, index) => {
      const x = index * (barWidth + barSpacing);
      const barHeight = Math.min(amplitude, height * 0.8);
      const y = (height - barHeight) / 2;

      // Determinar cor baseada no progresso e tema
      const isPlayed = index / waveformDataState.length <= progress;

      let barColor: string;
      if (isFromMe) {
        // Mensagens do usuário
        if (isDarkTheme) {
          // Modo escuro - usar maior contraste
          barColor = isPlayed
            ? "rgba(255, 255, 255, 1)"
            : "rgba(255, 255, 255, 0.15)";
        } else {
          // Modo claro - usar roxo escuro para progresso
          barColor = isPlayed ? "#6B2DD9" : "rgba(139, 69, 255, 0.2)"; // Roxo mais escuro
        }
      } else {
        // Mensagens do ChatJess - roxo para ambos os temas
        if (isDarkTheme) {
          // Modo escuro - roxo escuro para progresso
          barColor = isPlayed ? "#6B2DD9" : "rgba(139, 69, 255, 0.15)"; // Roxo mais escuro
        } else {
          // Modo claro - roxo escuro para progresso
          barColor = isPlayed ? "#6B2DD9" : "rgba(139, 69, 255, 0.2)"; // Roxo mais escuro
        }
      }

      ctx.fillStyle = barColor;

      // Usar fillRect comum para máxima compatibilidade (Chrome antigo não suporta roundRect)
      ctx.fillRect(x, y, barWidth, barHeight);
    });
  }, [waveformDataState, currentTime, duration, isFromMe, isDarkTheme]);

  useEffect(() => {
    if (waveformDataState.length > 0) {
      drawWaveform();
    }
  }, [waveformDataState, currentTime, duration, isFromMe, drawWaveform]);

  if (isAnalyzing) {
    return (
      <div className={`${styles.waveformContainer} ${className}`}>
        <div className={styles.analyzingIndicator}>
          <div className={styles.analyzingDot}></div>
          <div className={styles.analyzingDot}></div>
          <div className={styles.analyzingDot}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.waveformContainer} ${className}`}>
      <canvas
        ref={canvasRef}
        width={240}
        height={24}
        className={styles.waveformCanvas}
        onClick={handleCanvasClick}
        style={{ cursor: onSeek ? "pointer" : "default" }}
      />
    </div>
  );
};

export default AudioWaveform;
